/**
 * MAS / PackoutMAS forecast grid.
 *
 * Layout matches the FDPUpdated mock pixel-by-pixel — including the
 * **CMOP / non-CMOP partition** with a subtotal row:
 *
 *   ┌─────────── 3 sticky-left columns ───────────┬── 15 month columns ──┐
 *   │ Facility / COT │ Annual Vol (EUs) │ % of Mkt │ May'26 │ Jun'26 │ … │
 *   ├────────────────┼──────────────────┼──────────┼────────┼────────┼   │
 *   │ Hines IL       │       9,813      │   90.0%  │ 50.00% │ 50.00% │ … │
 *   │ VA Facility    │         100      │    1.0%  │ 50.00% │ 50.00% │ … │
 *   │ CMOP Total     │       9,913      │   91.0%  │   ...  │   ...  │ … │  ← subtotal
 *   │ MTF            │         600      │    5.5%  │ 50.00% │ 50.00% │ … │
 *   │ TMOP           │         387      │    3.5%  │ 50.00% │ 50.00% │ … │
 *   │ TOTAL          │      10,900      │   100%   │   ...  │   ...  │ … │  ← grand total
 *   └─────────────────────────────────────────────────────────────────────┘
 *
 * Partition rule mirrors the backend's NC_DOD_FACILITY_COTS constant:
 *   - non-CMOP = {MTF, TMOP}
 *   - CMOP     = everything else (Hines IL, VA Facility, any future
 *                CMOP-named facility seeded into AnnualMarketSales).
 *
 * The subtotal row is hidden when either group is empty — it would be
 * redundant with the grand TOTAL row in that case.
 *
 * "% of Mkt" is the row's share of total Annual Market across ALL
 * facilities (not just its own group), so CMOP rows + non-CMOP rows
 * sum to 100% via the grand TOTAL line.
 */

import { observer } from 'mobx-react';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';

import { useEditStore } from './editStoreContext';
import styles from './masForecastGrid.module.scss';

// Mirrors backend `NC_DOD_FACILITY_COTS` in
// fdp_app/utilities/provisional_forecast_builders.py — DoD-bucket
// facilities that also serve as the "non-CMOP" partition in the
// MAS / PackoutMAS grid. Keeping the constant duplicated rather
// than importing because the partition is a *display* concern,
// not a data concern; if the backend rule evolves these two lists
// can drift independently.
const NON_CMOP_FACILITY_COTS = new Set(['MTF', 'TMOP']);

const MasForecastGrid = observer(() => {
  const store = useEditStore();
  const slice = store.activeSlice;
  if (!slice) return null;

  // Show working copy if user has edited; otherwise pristine server state.
  const data = store.workingByNDC.get(slice.ndc) ?? slice.serverData;
  const months = data.data[0]?.forecast_data.map(m => m.forecast_month) ?? [];

  // Ramp-group label per row comes straight off the entry now —
  // `data[].group` is stamped by the backend builder (see
  // MAS_FACILITY_COT_TO_GROUP in provisional_forecast_builders.py) so
  // padded zero rows (CoTs missing from AMS) carry the label too. The
  // previous market_data-derived lookup is gone because market_data only
  // contains AMS-present CoTs and would skip the padded ones.

  if (data.data.length === 0) {
    return (
      <div className={styles.empty}>
        No facility-COT rows for this NDC. Check that AnnualMarketSales has
        data for this GFC.
      </div>
    );
  }

  // entry.annual_market / entry.eu are kept in sync with the
  // calc-selected packs by editStore.togglePackSelected, so reading
  // them directly here is correct — toggling a chip flows through
  // the working copy and re-renders this component.
  //
  // Subtotal / TOTAL math EXCLUDES rows the user has unchecked
  // (is_facility_selected=false). The row itself still renders so
  // the user can re-check it; only the sums skip it. % of Mkt for
  // each individual row stays computed against the SELECTED total
  // so the percentages add up to 100 across visible-selected rows.
  const selectedIdxs = data.data
    .map((entry, idx) => (entry.is_facility_selected ? idx : -1))
    .filter(i => i >= 0);
  const totalAnnualMarket = selectedIdxs.reduce(
    (s, i) => s + data.data[i].annual_market,
    0
  );
  const totalAnnualEU = selectedIdxs.reduce(
    (s, i) => s + data.data[i].eu,
    0
  );
  const monthlyEUMatrix: number[][] = data.data.map(entry =>
    entry.forecast_data.map(m => (entry.eu * m.forecast_percentage) / 100)
  );
  const monthlyEUTotals: number[] = months.map((_, mIdx) =>
    selectedIdxs.reduce((s, i) => s + monthlyEUMatrix[i][mIdx], 0)
  );

  // Partition row indices by CMOP membership. We keep the original
  // index because the percentage input + monthlyEUMatrix lookup are
  // keyed by it — re-numbering would break the existing edit path.
  const cmopIdxs: number[] = [];
  const nonCmopIdxs: number[] = [];
  data.data.forEach((entry, idx) => {
    if (NON_CMOP_FACILITY_COTS.has(entry.facility_cot_agency)) {
      nonCmopIdxs.push(idx);
    } else {
      cmopIdxs.push(idx);
    }
  });

  // CMOP subtotal — sums only the selected rows in the CMOP partition.
  const cmopSelectedIdxs = cmopIdxs.filter(
    i => data.data[i].is_facility_selected
  );
  const cmopAnnualMarket = cmopSelectedIdxs.reduce(
    (s, i) => s + data.data[i].annual_market,
    0
  );
  const cmopAnnualEU = cmopSelectedIdxs.reduce(
    (s, i) => s + data.data[i].eu,
    0
  );
  const cmopMonthlyEUTotals = months.map((_, mIdx) =>
    cmopSelectedIdxs.reduce((s, i) => s + monthlyEUMatrix[i][mIdx], 0)
  );
  const cmopPctOfMkt =
    totalAnnualMarket > 0
      ? ((cmopAnnualMarket / totalAnnualMarket) * 100).toFixed(1)
      : '0.0';

  // Skip the subtotal row when either group is empty — it'd be a
  // visually duplicate of TOTAL.
  const showCmopSubtotal = cmopIdxs.length > 0 && nonCmopIdxs.length > 0;

  const renderDataRow = (rowIdx: number) => {
    const entry = data.data[rowIdx];
    const isSelected = entry.is_facility_selected;
    // % of Mkt is shown vs the SELECTED total — unchecked rows render
    // a dash because they don't participate in the denominator.
    const pctOfMkt =
      isSelected && totalAnnualMarket > 0
        ? ((entry.annual_market / totalAnnualMarket) * 100).toFixed(1)
        : '0.0';
    const checkboxId = `is-fac-sel-${slice.ndc}-${rowIdx}`;
    return (
      <tr
        key={`${entry.facility_cot_agency}-${rowIdx}`}
        className={isSelected ? undefined : styles.unselectedRow}
      >
        <td className={`${styles.bodyCell} ${styles.stickyFacility} ${styles.facilityHead}`}>
          <div className={styles.facilityCellInner}>
            <Checkbox
              inputId={checkboxId}
              checked={isSelected}
              onChange={e =>
                store.setFacilitySelected(slice.ndc, rowIdx, !!e.checked)
              }
            />
            <div className={styles.facilityText}>
              <label htmlFor={checkboxId} className={styles.facilityLabel}>
                {entry.facility_cot_agency}
              </label>
              {entry.group && (
                <div className={styles.facilityGroup}>{entry.group}</div>
              )}
            </div>
          </div>
        </td>
        <td className={`${styles.bodyCell} ${styles.stickyAnnual} ${styles.numericCell}`}>
          {entry.eu > 0 ? formatInt(entry.eu) : '–'}
        </td>
        <td className={`${styles.bodyCell} ${styles.stickyPct} ${styles.numericCell} ${styles.dim}`}>
          {isSelected && entry.annual_market > 0 ? `${pctOfMkt}%` : '–'}
        </td>
        {entry.forecast_data.map((month, monthIdx) => {
          const eu = monthlyEUMatrix[rowIdx][monthIdx];
          // Every month is user-editable. Editing the per-FacilityCOT
          // anchor month still cascades its value to every later month
          // (handled inside store.setMonthPercentageWithCascade);
          // editing any other month updates just that cell.
          return (
            <td key={month.forecast_month} className={styles.bodyCell}>
              <div className={styles.cellStack}>
                <div className={styles.percentRow}>
                  <InputText
                    className={styles.percentInput}
                    value={formatPercent(month.forecast_percentage)}
                    onChange={e => {
                      const next = parseFloat(e.target.value);
                      store.setMonthPercentageWithCascade(
                        slice.ndc,
                        rowIdx,
                        monthIdx,
                        Number.isFinite(next) ? next : 0
                      );
                    }}
                  />
                  <span className={styles.percentSign}>%</span>
                </div>
                <div className={styles.derivedEU}>
                  {eu > 0 ? formatDerivedEU(eu) : '–'}
                </div>
              </div>
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className={styles.wrap}>
      <h4 className={styles.heading}>CMOP &amp; COT Forecast Parameters</h4>
      <div className={styles.scrollWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={`${styles.headCell} ${styles.stickyFacilityHead}`}>
                Facility / COT
              </th>
              <th className={`${styles.headCell} ${styles.stickyAnnualHead}`}>
                Annual Vol (EUs)
              </th>
              <th className={`${styles.headCell} ${styles.stickyPctHead}`}>
                % of Mkt
              </th>
              {months.map(m => (
                <th key={m} className={styles.headCell}>
                  {formatMonthLabel(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* CMOP group rows */}
            {cmopIdxs.map(renderDataRow)}

            {/* CMOP subtotal — only when both groups have at least one row */}
            {showCmopSubtotal && (
              <tr className={styles.subtotalRow}>
                <td className={`${styles.bodyCell} ${styles.stickyFacility} ${styles.subtotalCell} ${styles.subtotalLabel}`}>
                  CMOP Total
                </td>
                <td className={`${styles.bodyCell} ${styles.stickyAnnual} ${styles.numericCell} ${styles.subtotalCell}`}>
                  {cmopAnnualEU > 0 ? formatInt(cmopAnnualEU) : '–'}
                </td>
                <td className={`${styles.bodyCell} ${styles.stickyPct} ${styles.numericCell} ${styles.subtotalCell} ${styles.dim}`}>
                  {cmopAnnualMarket > 0 ? `${cmopPctOfMkt}%` : '–'}
                </td>
                {cmopMonthlyEUTotals.map((eu, mIdx) => (
                  <td
                    key={mIdx}
                    className={`${styles.bodyCell} ${styles.numericCell} ${styles.subtotalCell}`}
                  >
                    {eu > 0 ? formatDerivedEU(eu) : '–'}
                  </td>
                ))}
              </tr>
            )}

            {/* Non-CMOP group rows */}
            {nonCmopIdxs.map(renderDataRow)}

            {/* Grand total */}
            <tr className={styles.totalRow}>
              <td className={`${styles.bodyCell} ${styles.stickyFacility} ${styles.totalCell}`}>
                TOTAL
              </td>
              <td className={`${styles.bodyCell} ${styles.stickyAnnual} ${styles.numericCell} ${styles.totalCell}`}>
                {formatInt(totalAnnualEU)}
              </td>
              <td className={`${styles.bodyCell} ${styles.stickyPct} ${styles.numericCell} ${styles.totalCell}`}>
                {totalAnnualMarket > 0 ? '100%' : '–'}
              </td>
              {monthlyEUTotals.map((total, mIdx) => (
                <td
                  key={mIdx}
                  className={`${styles.bodyCell} ${styles.numericCell} ${styles.totalCell}`}
                >
                  {total > 0 ? formatDerivedEU(total) : '–'}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <p className={styles.footnote}>
        CMOP rows: months 1–4 editable; month 4 cascades to months 5–15.
        MTF / TMOP / VISN rows: only month 1 editable; cascades to months 2–15.
      </p>
    </div>
  );
});

// --------------------------------------------------------------------------- //
// Formatters                                                                  //
// --------------------------------------------------------------------------- //
function formatMonthLabel(yyyymmdd: string): string {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(yyyymmdd);
  if (!m) return yyyymmdd;
  const year = m[1].slice(2);
  const monthIdx = Math.max(0, Math.min(11, parseInt(m[2], 10) - 1));
  return `${SHORT_MONTHS[monthIdx]} '${year}`;
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return '';
  const rounded = Math.round(value * 100) / 100;
  return rounded.toString();
}

function formatInt(value: number): string {
  if (!Number.isFinite(value)) return '–';
  return Math.round(value).toLocaleString();
}

function formatDerivedEU(value: number): string {
  if (!Number.isFinite(value)) return '–';
  return Math.round(value).toLocaleString();
}

export default MasForecastGrid;
